<template>
  <div class="card mt-4 shadow-sm">
    <div class="card-header bg-white">
      <h5 class="mb-0"><i class="fa fa-gift me-2"></i>Refer Friends, Earn Asher Points</h5>
    </div>
    <div class="card-body">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-else-if="error" class="alert alert-danger mb-0">{{ error }}</div>

      <div v-else>
        <div class="row g-3 mb-3">
          <div class="col-12 col-md-6">
            <label class="form-label text-muted small mb-1">Your referral code</label>
            <div class="input-group">
              <input type="text" class="form-control fw-bold" :value="dashboard.referralCode" readonly>
              <button class="btn btn-outline-primary" @click="copyLink" :disabled="copying">
                <span v-if="copying" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="fa fa-copy me-1"></i>{{ copying ? 'Copying...' : (copySuccess ? 'Copied!' : 'Copy Link') }}
              </button>
              <button class="btn btn-outline-secondary" @click="shareLink" :disabled="sharing">
                <span v-if="sharing" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="fa fa-share-alt me-1"></i>{{ sharing ? 'Sharing...' : 'Share' }}
              </button>
            </div>
          </div>
          <div class="col-12 col-md-6">
            <label class="form-label text-muted small mb-1">Your Asher Points balance</label>
            <div class="fs-3 fw-bold text-primary">{{ dashboard.asherPointsBalance ?? 0 }}</div>
          </div>
        </div>

        <div class="border rounded p-3 mb-4 bg-color">
          <label class="form-label small mb-2">Send your invite directly</label>
          <div class="row g-2 align-items-center">
            <div class="col-12 col-sm-auto">
              <select class="form-select form-select-sm" v-model="inviteIsEmail">
                <option :value="true">Email</option>
                <option :value="false">Phone</option>
              </select>
            </div>
            <div class="col">
              <input type="text" class="form-control form-control-sm"
                     :placeholder="inviteIsEmail ? 'friend@example.com' : '(555) 555-5555'"
                     v-model="inviteContact">
            </div>
            <div class="col-12 col-sm-auto">
              <button class="btn btn-primary btn-sm w-100" @click="sendInvite" :disabled="inviteSending || !inviteContact">
                <span v-if="inviteSending" class="spinner-border spinner-border-sm me-1"></span>
                {{ inviteSending ? 'Sending...' : 'Send Invite' }}
              </button>
            </div>
          </div>
          <div v-if="inviteError" class="alert alert-danger mt-2 mb-0 py-1 px-2 small">{{ inviteError }}</div>
          <div v-if="inviteSuccess" class="alert alert-success mt-2 mb-0 py-1 px-2 small">{{ inviteSuccess }}</div>
        </div>

        <h6 class="mb-2">People you've invited</h6>
        <div v-if="!dashboard.invitedReferrals || dashboard.invitedReferrals.length === 0" class="text-muted small">
          No invites yet — share your code above to get started.
        </div>
        <ul v-else class="list-group list-group-flush">
          <li v-for="referral in dashboard.invitedReferrals" :key="referral.referralId" class="list-group-item px-0">
            <div class="d-flex justify-content-between align-items-center" style="cursor:pointer;" @click="toggleExpanded(referral.referralId)">
              <div>
                <span class="fw-semibold">{{ referral.referredDisplayName }}</span>
                <span class="badge rounded-pill ms-2" :class="statusBadgeClass(referral.status)">{{ statusLabel(referral.status) }}</span>
              </div>
              <i class="fa" :class="isExpanded(referral.referralId) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </div>
            <div class="progress mt-2" style="height: 6px;" v-if="referral.requiredPaidRides > 0">
              <div class="progress-bar" role="progressbar"
                   :style="{ width: progressPercent(referral) + '%' }"></div>
            </div>
            <div class="small text-muted mt-1 d-flex justify-content-between flex-wrap gap-2"
                 v-if="referral.requiredPaidRides > 0">
              <span>{{ referral.completedPaidRides }}/{{ referral.requiredPaidRides }} paid rides</span>
              <span v-if="deadlineLabel(referral)" :class="deadlineIsUrgent(referral) ? 'text-danger' : ''">
                <i class="fa fa-clock me-1"></i>{{ deadlineLabel(referral) }}
              </span>
            </div>
            <div v-if="outstandingLabel(referral)" class="small mt-1" :class="outstandingClass(referral)">
              <i class="fa me-1" :class="outstandingIcon(referral)"></i>{{ outstandingLabel(referral) }}
            </div>

            <div v-if="isExpanded(referral.referralId)" class="mt-2 ps-2 border-start">
              <div class="small text-muted mb-1">Signed up {{ formatDate(referral.signedUpAt) }}</div>
              <div v-if="!referral.rewards || referral.rewards.length === 0" class="small text-muted">No rewards yet.</div>
              <div v-else v-for="(reward, idx) in referral.rewards" :key="idx" class="small d-flex justify-content-between mb-1">
                <span>{{ milestoneLabel(reward.milestone) }} &mdash; {{ rewardAmountLabel(reward) }}</span>
                <span class="badge rounded-pill" :class="rewardBadgeClass(reward.status)">{{ rewardStatusLabel(reward.status) }}</span>
              </div>
            </div>
          </li>
        </ul>

        <div class="alert alert-info mt-4 mb-0 small">
          <i class="fa fa-mobile-alt me-2"></i>Referrals in the Asher mobile app are coming soon &mdash; for now, share your link here.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import moment from 'moment/moment';
import {
  outstandingLabel as resolveOutstandingLabel,
  outstandingClass,
  outstandingIcon,
  deadlineIsUrgent,
  deadlineLabel,
} from '../utility/referralOutstanding';

const loading = ref(true);
const error = ref(null);
const dashboard = ref({ referralCode: '', referralLink: '', invitedReferrals: [], asherPointsBalance: null });

const copying = ref(false);
const copySuccess = ref(false);
const sharing = ref(false);

const inviteContact = ref('');
const inviteIsEmail = ref(true);
const inviteSending = ref(false);
const inviteError = ref(null);
const inviteSuccess = ref(null);

const expandedIds = ref(new Set());

const statusLabels = {
  SIGNED_UP: 'Signed Up',
  IN_PROGRESS: 'In Progress',
  QUALIFIED: 'Qualified',
  REWARDED: 'Rewarded',
  EXPIRED: 'Expired',
  REJECTED: 'Rejected',
  FRAUD_REVIEW: 'Under Review',
};

const statusBadgeClasses = {
  SIGNED_UP: 'bg-secondary',
  IN_PROGRESS: 'bg-info text-dark',
  QUALIFIED: 'bg-warning text-dark',
  REWARDED: 'bg-success',
  EXPIRED: 'bg-secondary',
  REJECTED: 'bg-secondary',
  FRAUD_REVIEW: 'bg-secondary',
};

const rewardStatusLabels = {
  AVAILABLE: 'Pending',
  REDEEMED: 'Earned',
  EXPIRED: 'Expired',
  REVERSED: 'Reversed',
};

const rewardBadgeClasses = {
  AVAILABLE: 'bg-warning text-dark',
  REDEEMED: 'bg-success',
  EXPIRED: 'bg-secondary',
  REVERSED: 'bg-secondary',
};

const milestoneLabels = {
  FIRST_PAID_RIDE: 'First Ride',
  FIVE_PAID_RIDES: '5-Ride Bonus',
  DRIVER_QUALIFICATION: 'Qualification',
};

const statusLabel = (status) => statusLabels[status] || status;
const statusBadgeClass = (status) => statusBadgeClasses[status] || 'bg-secondary';
const rewardStatusLabel = (status) => rewardStatusLabels[status] || status;
const rewardBadgeClass = (status) => rewardBadgeClasses[status] || 'bg-secondary';
const milestoneLabel = (milestone) => milestoneLabels[milestone] || milestone;

const rewardAmountLabel = (reward) => {
  if (reward.rewardType === 'ASHER_POINTS') return `+${reward.points ?? 0} points`;
  if (reward.rewardType === 'SUBSCRIPTION_DISCOUNT_WEEK') return `${reward.discountPercent ?? 0}% off ${reward.discountWeekCount ?? 0} week(s)`;
  return '';
};

const outstandingLabel = (referral) => resolveOutstandingLabel(referral, false);

const progressPercent = (referral) => {
  if (!referral.requiredPaidRides) return 0;
  return Math.min(100, Math.round((referral.completedPaidRides / referral.requiredPaidRides) * 100));
};

const isExpanded = (id) => expandedIds.value.has(id);
const toggleExpanded = (id) => {
  if (expandedIds.value.has(id)) {
    expandedIds.value.delete(id);
  } else {
    expandedIds.value.add(id);
  }
  expandedIds.value = new Set(expandedIds.value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parsed = moment(dateStr);
  return parsed.isValid() ? parsed.format('MMM Do, YYYY') : '';
};

const fetchDashboard = async () => {
  loading.value = true;
  error.value = null;
  try {
    const response = await fetch('/api/portal/rider/referrals');
    if (!response.ok) {
      throw new Error('Unable to load your referral dashboard.');
    }
    dashboard.value = await response.json();
  } catch (e) {
    error.value = e.message || 'Failed to load referral dashboard.';
  } finally {
    loading.value = false;
  }
};

const copyLink = async () => {
  copying.value = true;
  try {
    await navigator.clipboard.writeText(dashboard.value.referralLink);
    copySuccess.value = true;
    setTimeout(() => { copySuccess.value = false; }, 2000);
  } catch (e) {
    error.value = null;
  } finally {
    copying.value = false;
  }
};

const shareLink = async () => {
  sharing.value = true;
  try {
    if (navigator.share) {
      await navigator.share({
        title: 'Join Asher Rides',
        text: 'Use my referral link to join Asher Rides!',
        url: dashboard.value.referralLink,
      });
    } else {
      await navigator.clipboard.writeText(dashboard.value.referralLink);
      copySuccess.value = true;
      setTimeout(() => { copySuccess.value = false; }, 2000);
    }
  } catch (e) {
    // User cancelled the native share sheet, or clipboard is unavailable — not an error to surface.
  } finally {
    sharing.value = false;
  }
};

const sendInvite = async () => {
  if (!inviteContact.value) return;
  inviteSending.value = true;
  inviteError.value = null;
  inviteSuccess.value = null;
  try {
    const response = await fetch('/api/portal/rider/referrals/invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userIdentifier: inviteContact.value, isEmail: inviteIsEmail.value }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Unable to send invite.');
    }
    inviteSuccess.value = 'Invite sent!';
    inviteContact.value = '';
  } catch (e) {
    inviteError.value = e.message || 'Unable to send invite.';
  } finally {
    inviteSending.value = false;
  }
};

onMounted(fetchDashboard);
</script>
